// Small math the engine, the site, and the tools share: clamps, a linear mix, a smooth step,
// the fractional part, and two integer hashes. Shaders keep their own GLSL copies of what they
// use (face-edge.ts). `Math.clamp` is a TC39 proposal that no browser ships yet.

/** `value` held in [low, high]. NaN stays NaN, so a bad input is easy to spot. */
export function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/** `value` held in [0, 1]. */
export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

/** The point `t` of the way from `a` to `b`. A `t` outside [0, 1] goes past the ends. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** GLSL's smoothstep: 0 at or before `edge0`, 1 at or after `edge1`, and the cubic
    `3t^2 - 2t^3` in between. Equal edges give 0 before the edge, 1 after it, and NaN right
    on it (GLSL leaves equal edges undefined). */
export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

/** The fractional part of `x`, in [0, 1), like GLSL's fract. */
export function fract(x: number): number {
  return x - Math.floor(x);
}

/** A hash of a whole number, in [0, 1): the lowbias32 mix. It's all integer math, which every
    GPU does exactly the way JavaScript does, so the GLSL copy (face-edge.ts) gives the same
    answers. A `sin()` hash was off by up to 0.45 on one GPU. Negative numbers wrap to 32 bits,
    as in GLSL. */
export function hash1(n: number): number {
  let x = n | 0;
  x = Math.imul(x ^ (x >>> 16), 0x7feb352d);
  x = Math.imul(x ^ (x >>> 15), 0x846ca68b);
  return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
}

/** A hash of a place, in [0, 1): the same place always gets the same value. It rounds to whole
    numbers first, so subpixel layout doesn't change it. */
export function hash2(x: number, y: number): number {
  let hash = Math.imul(Math.round(x), 374761393) ^ Math.imul(Math.round(y), 668265263);
  hash = Math.imul(hash ^ (hash >>> 13), 1274126177);
  hash ^= hash >>> 16;
  return (hash >>> 0) / 4294967296;
}
